import { FUSO_EXIBICAO } from '../../admin/shared';
import { type OpcaoFiltro } from 'src/app/shared/filtros-dinamicos';

/**
 * Filtros da tela de check-in — o que é DESTA tela.
 *
 * A mecânica do filtro dinâmico (catálogo → chips, prefixo da barra, query em
 * `campo[operador]=valor`) saiu daqui para
 * `src/app/shared/filtros-dinamicos` quando a tela de meus eventos passou a
 * usar a mesma toolbar; a documentação de COMO o filtro funciona no fio mora
 * lá. Este arquivo reexporta aquela superfície para quem já importava daqui,
 * e guarda o que é só do check-in.
 *
 * ## O que continua fixo aqui
 *
 * `ORDENACOES` (via `sort` do Pageable) e sessão (`sessaoId`) — são params
 * nomeados do contrato, não passam pelo catálogo.
 */
export * from 'src/app/shared/filtros-dinamicos';

/**
 * FALLBACK das ordenações, usado só até o catálogo de `/meta` chegar.
 *
 * Quem manda agora é `GET /api/v3/eventos/{eventoId}/ingressos/meta`
 * (`guedder-api#349`/`#350`), que publica estas mesmas quatro. A lista local
 * permanece porque a toolbar não pode nascer sem opção — o chip sumiria no
 * primeiro frame e voltaria piscando.
 *
 * ## Os rótulos de `dataCheckin` estavam errados, e agora dizem a verdade
 *
 * A versão anterior chamava o `desc` de "Pendentes primeiro", com a lógica de
 * que `dataCheckin` é nulo para quem não entrou e o Postgres ordena `DESC` com
 * NULLS FIRST. A premissa não vale aqui: o `parseSort` do `IngressoControllerV3`
 * sempre aplicou `.nullsLast()`, e um IT do backend contra o Postgres real
 * mostra o `desc` devolvendo `[mais recente, …, pendente]` — pendentes por
 * ÚLTIMO, nas duas direções.
 *
 * Ou seja, o chip prometia o oposto do que entregava. Ninguém percebeu porque
 * uma lista fora de ordem não parece um erro.
 *
 * Consequência real, registrada para quem for decidir: a portaria perdeu o
 * default útil que o rótulo antigo descrevia ("numa portaria, a lista útil é a
 * de quem ainda não entrou"). Recuperá-lo exige uma ordenação com nulos
 * PRIMEIRO no backend, que não existe hoje — não é coisa que um rótulo resolva.
 */
export const ORDENACOES: OpcaoFiltro[] = [
  { valor: 'dataCheckin,desc', rotulo: 'Check-in mais recente' },
  { valor: 'dataCheckin,asc', rotulo: 'Check-in mais antigo' },
  { valor: 'nomeParticipante,asc', rotulo: 'Nome (A–Z)' },
  { valor: 'nomeParticipante,desc', rotulo: 'Nome (Z–A)' },
];

const DIA_HORA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO_EXIBICAO,
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * `SessaoVO` não tem nome — só `intervalo` — então o rótulo da sessão é o
 * horário dela, no fuso do evento.
 */
export function rotuloSessao(sessao: Record<string, unknown>, indice: number): string {
  const intervalo = sessao['intervalo'] as { start?: string; end?: string } | undefined;
  const inicio = intervalo?.start;
  if (!inicio) return `Sessão ${indice + 1}`;
  const d = new Date(inicio);
  if (Number.isNaN(d.getTime())) return `Sessão ${indice + 1}`;
  return DIA_HORA.format(d).replace(',', '').replace('.', '');
}

export function sessoesParaOpcoes(brutas: unknown): OpcaoFiltro[] {
  if (!Array.isArray(brutas)) return [];
  return brutas
    .map((s, i) => {
      const sessao = s as Record<string, unknown>;
      const id = sessao['id'];
      return id ? { valor: String(id), rotulo: rotuloSessao(sessao, i) } : null;
    })
    .filter((o): o is OpcaoFiltro => o !== null);
}
